import { useCompanion } from "./CompanionProvider";
import type { CompanionPresence } from "../../../lib/companion";

interface CompanionHeaderStatusViewProps {
  presence: CompanionPresence;
  open: boolean;
  onToggle: () => void;
  dark?: boolean;
}

/** Separate Companion state/action beside the persistent Ask input. */
export function CompanionHeaderStatus({ dark = false }: { dark?: boolean }) {
  const companion = useCompanion();
  const presence = companion?.data?.presence;

  if (!companion?.enabled || !presence) return null;

  return (
    <CompanionHeaderStatusView
      presence={presence}
      open={companion.open}
      onToggle={() => companion.setOpen(!companion.open)}
      dark={dark}
    />
  );
}

/** Pure status button used by the live Companion wrapper and visual QA. */
export function CompanionHeaderStatusView({
  presence,
  open,
  onToggle,
  dark = false,
}: CompanionHeaderStatusViewProps) {

  const needsYou = presence.needs_you_count;
  const hasUpdates = presence.count > 0;
  const attention = needsYou > 0 || presence.tier === "interrupt";

  const label = attention
    ? `${Math.max(1, needsYou)} decision${Math.max(1, needsYou) === 1 ? "" : "s"} need${Math.max(1, needsYou) === 1 ? "s" : ""} you`
    : hasUpdates
      ? `${presence.count} update${presence.count === 1 ? "" : "s"}`
      : "All quiet";

  const action = attention ? "Review" : hasUpdates ? "View" : null;
  const tone = attention
    ? "border-amber-200 bg-amber-50/80 text-amber-950 hover:border-amber-300 hover:bg-amber-100/80"
    : hasUpdates
      ? "border-cyan-200 bg-cyan-50/80 text-cyan-950 hover:border-cyan-300 hover:bg-cyan-100/80"
      : dark
        ? "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
        : "border-slate-200 bg-slate-50 text-slate-600 hover:bg-slate-100";
  const dot = attention
    ? "bg-amber-400"
    : hasUpdates
      ? "bg-cyan-600"
      : "bg-emerald-500";
  const actionTone = attention
    ? "text-amber-700"
    : hasUpdates
      ? "text-cyan-700"
      : "text-slate-500";

  return (
    <button
      type="button"
      onClick={onToggle}
      className={`inline-flex h-[34px] shrink-0 items-center gap-2 rounded-lg border px-3 text-[12px] font-semibold transition-colors ${tone}`}
      aria-label={
        attention
          ? `${label}. Open Meridian Companion to review.`
          : hasUpdates
            ? `${label}. Open Meridian Companion.`
            : "Meridian Companion is all quiet. Open Companion."
      }
      aria-expanded={open}
      title={presence.digest}
    >
      <span className={`h-2 w-2 shrink-0 rounded-full ${dot}`} aria-hidden />
      <span className="whitespace-nowrap">{label}</span>
      {action && (
        <>
          <span className="h-4 w-px bg-current opacity-15" aria-hidden />
          <span className={`whitespace-nowrap ${actionTone}`}>{action}</span>
        </>
      )}
    </button>
  );
}
